/**
 * Utility for tracking and updating user's Last_Online status
 * Updates only once per day to avoid unnecessary API calls
 */

import apiService from '@/lib/api-functions/api-service';

const LAST_ONLINE_KEY = 'lastOnlineUpdate';

/**
 * Check if the last online update was today
 */
function wasUpdatedToday(): boolean {
  const lastUpdate = localStorage.getItem(LAST_ONLINE_KEY);
  if (!lastUpdate) return false;

  const lastUpdateDate = new Date(lastUpdate);
  const today = new Date();

  // Check if it's the same day
  return (
    lastUpdateDate.getFullYear() === today.getFullYear() &&
    lastUpdateDate.getMonth() === today.getMonth() &&
    lastUpdateDate.getDate() === today.getDate()
  );
}

/**
 * Update the user's Last_Online timestamp
 * Only updates if not already updated today
 * @param force - Force update even if already updated today
 * @returns true if updated, false if skipped
 */
export async function updateLastOnline(force: boolean = false): Promise<boolean> {
  // Skip if already updated today (unless forced)
  if (!force && wasUpdatedToday()) {
    return false;
  }

  try {
    const now = new Date().toISOString();

    const response = await apiService.updateProfile({
      Last_Online: now,
    });

    if (response.success) {
      // Store the update time in localStorage
      localStorage.setItem(LAST_ONLINE_KEY, now);
      return true;
    } else {
      console.error('Failed to update Last_Online:', response.error);
      return false;
    }
  } catch (error) {
    console.error('Error updating Last_Online:', error);
    return false;
  }
}

/**
 * Clear the last online tracking (useful for logout)
 */
export function clearLastOnlineTracking(): void {
  localStorage.removeItem(LAST_ONLINE_KEY);
}

/**
 * Get the last update timestamp from localStorage
 */
export function getLastUpdateTime(): Date | null {
  const lastUpdate = localStorage.getItem(LAST_ONLINE_KEY);
  return lastUpdate ? new Date(lastUpdate) : null;
}

